import React, { useState } from 'react';
import Modal from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import SearchInput from '../../../../components/ui/SearchInput';
import { useChatContactPicker } from '../../hooks/useChatContactPicker';
import { pickItemToFormInput } from '../../lib/chatContactPicker';
import { ChatContactPickRow } from './ChatContactPickRow';

export interface ChatContactFormInput {
    fullName: string;
    phoneNumber: string;
    organization?: string;
    email?: string;
}

interface ChatContactFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (input: ChatContactFormInput) => Promise<void>;
}

export const ChatContactForm: React.FC<ChatContactFormProps> = ({ isOpen, onClose, onSubmit }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {
        query,
        setQuery,
        selectedKey,
        selectKey,
        you,
        contacts,
        isLoading,
        selectedItem,
        reset,
    } = useChatContactPicker(isOpen);

    const handleClose = () => {
        if (isSubmitting) return;
        reset();
        onClose();
    };

    const handleSend = async () => {
        if (!selectedItem || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await onSubmit(pickItemToFormInput(selectedItem));
            reset();
        } catch {
            // erro já notificado pela camada de envio — mantém formulário aberto
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="sm"
            className="h-[766px] w-[350px] max-h-[90vh] max-w-[350px] sm:w-[350px]"
        >
            <Modal.Header onClose={handleClose}>Enviar contato</Modal.Header>
            <Modal.Body noPadding className="flex min-h-0 flex-1 flex-col space-y-0">
                <div className="shrink-0 px-s4 py-s3">
                    <SearchInput
                        value={query}
                        onChange={setQuery}
                        placeholder="Pesquisar nome ou número"
                    />
                </div>
                <ul
                    role="listbox"
                    aria-label="Selecionar contato"
                    className="min-h-0 flex-1 list-none overflow-y-auto p-0"
                >
                    {you && (
                        <li role="group" aria-labelledby="chat-contact-you-heading" className="list-none">
                            <h2 id="chat-contact-you-heading" className="px-s4 py-s2 text-xs text-muted">
                                Você
                            </h2>
                            <ul role="presentation" className="list-none p-0">
                                <ChatContactPickRow
                                    item={you}
                                    selected={selectedKey === you.key}
                                    onSelect={selectKey}
                                />
                            </ul>
                        </li>
                    )}
                    <li role="group" aria-labelledby="chat-contact-list-heading" className="list-none">
                        <h2 id="chat-contact-list-heading" className="px-s4 py-s2 text-xs text-muted">
                            Contatos
                        </h2>
                        {isLoading ? (
                            <p className="px-s4 py-s3 text-sm text-muted">Carregando…</p>
                        ) : contacts.length === 0 ? (
                            <p className="px-s4 py-s3 text-sm text-muted">Nenhum contato com telefone.</p>
                        ) : (
                            <ul role="presentation" className="list-none p-0">
                                {contacts.map((item) => (
                                    <ChatContactPickRow
                                        key={item.key}
                                        item={item}
                                        selected={selectedKey === item.key}
                                        onSelect={selectKey}
                                    />
                                ))}
                            </ul>
                        )}
                    </li>
                </ul>
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" disabled={!selectedItem || isSubmitting} onClick={handleSend}>
                    {isSubmitting ? 'Enviando...' : 'Enviar contato'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ChatContactForm;
